// Breakdown por canal (§4.1): donut de enviados por canal (padrão
// visual do portal) + linhas clicáveis por canal → Consulta
// filtrada por channel. As linhas (HTML) são o alvo acessível.
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Pie, PieChart } from "recharts"

import type { ChartConfig } from "@/components/ui/chart"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import type {
  NotificationOverviewChannelType,
  NotificationSearchChannelType,
} from "@/types/notification-center"

import {
  buildNotificationSearchHref,
  formatDashboardInteger,
  formatDashboardRate,
} from "../dashboard-utils"
import { DashboardChartCard } from "./dashboard-chart-card"

// Cores por canal — variáveis de chart já existentes no portal.
const CHANNEL_COLORS: Record<
  NotificationSearchChannelType,
  string
> = {
  EMAIL: "hsl(var(--chart-email))",
  WHATSAPP: "hsl(var(--chart-whatsapp))",
  SMS: "hsl(var(--chart-4))",
}

interface DashboardChannelChartProps {
  byChannel?: NotificationOverviewChannelType[]
  isLoading: boolean
  hasError: boolean
  onRetry: () => void
  range: { startDate?: string; endDate?: string }
}

export const DashboardChannelChart = ({
  byChannel,
  isLoading,
  hasError,
  onRetry,
  range,
}: DashboardChannelChartProps) => {
  const { t, i18n } = useTranslation()
  const chartPrefix =
    "pages.notificationCenter.dashboardPage.charts.byChannel"

  const totalSent = (byChannel ?? []).reduce(
    (sum, item) => sum + item.sent,
    0
  )
  const chartData = (byChannel ?? []).map((item) => ({
    channel: item.channel,
    sent: item.sent,
    fill: CHANNEL_COLORS[item.channel],
  }))
  const chartConfig = Object.fromEntries(
    (byChannel ?? []).map((item) => [
      item.channel,
      {
        label: item.channel,
        color: CHANNEL_COLORS[item.channel],
      },
    ])
  ) satisfies ChartConfig

  return (
    <DashboardChartCard
      title={t(`${chartPrefix}.title`)}
      testId="dashboard-channel"
      isLoading={isLoading}
      hasError={hasError}
      isEmpty={!byChannel?.length}
      onRetry={onRetry}
    >
      <ChartContainer
        config={chartConfig}
        className="mx-auto aspect-square max-h-[200px]"
      >
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel />}
          />
          <Pie
            data={chartData}
            dataKey="sent"
            nameKey="channel"
            innerRadius={50}
          />
        </PieChart>
      </ChartContainer>
      <ul className="mt-3 space-y-1">
        {(byChannel ?? []).map((item) => (
          <li key={item.channel}>
            <Link
              to={buildNotificationSearchHref({
                channel: item.channel,
                ...range,
              })}
              aria-label={t(`${chartPrefix}.link`, {
                channel: item.channel,
              })}
              className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            >
              <span className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="size-2.5 shrink-0 rounded-sm"
                  style={{
                    backgroundColor:
                      CHANNEL_COLORS[item.channel],
                  }}
                />
                {item.channel}
              </span>
              <span className="tabular-nums text-muted-foreground">
                {formatDashboardInteger(
                  item.sent,
                  i18n.language
                )}
                {totalSent > 0 && (
                  <>
                    {" · "}
                    {formatDashboardRate(
                      item.sent / totalSent,
                      i18n.language
                    )}
                  </>
                )}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </DashboardChartCard>
  )
}
